import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useDuplicateSingleQuery } from '../../rest/useDuplicateCertificate';
import { getStatusLabel } from '../../Utils/Label/status-label';
import PageLoader from '../../Components/Spinner/PageLoader';
import ModalsWrapper from '../../Components/Modals/ModalsWrapper';
import PdfAndImageView from '../../Components/pdfAndImageView/PdfAndImageView';
import { CommonTypoGraphy } from '../../Components/pdfAndImageView/CommonTypoGraphy';
import OfflinePaymentCardInformation from '../new-member-applications/IndexFile/paymentInformation/offline/OfflinePaymentCardInformation';
import CommonDetailItem, {
  formatDate,
  formatValue,
} from '../../Components/CommonDetailItem/CommonDetailItem';

const SingleDuplicateCertificateApplication = () => {
  const { id } = useParams();
  const { data, fetchData, loading } = useDuplicateSingleQuery(
    id !== undefined ? id : '',
  );
  const [open, setOpen] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<string | null>('');

  useEffect(() => {
    fetchData();
  }, [fetchData, reload]);

  return (
    <>
      {!loading ? (
        <Box sx={{ width: '100%', mt: { xs: 4, sm: 5 }, mb: 3 }}>
          <Card
            sx={{
              width: '100%',
              borderRadius: 2,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              bgcolor: '#ffffff',
            }}
          >
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    fontWeight: 700,
                    color: '#1e293b',
                  }}
                >
                  Duplicate Certificate Application Details
                </Typography>
              }

              sx={{
                borderBottom: '1px solid #edf2f7',
                py: 1.25,
                px: { xs: 2, sm: 2.5 },
                backgroundColor: '#fafbfc',
              }}
            />
            <CardContent
              sx={{
                p: { xs: 2, sm: 2.5 },
                '&:last-child': { pb: { xs: 2, sm: 2.5 } },
              }}
            >
              {data ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Rejection / Note banner if any */}
                  {data?.duplicate_certificate_application_rejeceted_reason && (
                    <Alert
                      severity='error'
                      sx={{
                        borderRadius: 1.5,
                        py: 0.5,
                        px: 1.5,
                        fontSize: '0.875rem',
                        '& .MuiAlert-message': { py: 0.25 },
                      }}
                    >
                      <Typography
                        variant='subtitle2'
                        fontWeight='bold'
                        component='span'
                        sx={{ fontSize: '0.875rem' }}
                      >
                        Note / Rejection Reason:
                      </Typography>{' '}
                      {data.duplicate_certificate_application_rejeceted_reason}
                    </Alert>
                  )}

                  {/* Section 1: General Information */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 3.5,
                          height: 15,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        variant='subtitle2'
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          letterSpacing: 0.1,
                        }}
                      >
                        General Information
                      </Typography>
                    </Box>
                    <Grid container rowSpacing={0.75} columnSpacing={2}>

                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Application Date'
                        value={formatDate(
                          data.duplicate_certificate_application_created_at,
                        )}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Company Name'
                        value={formatValue(data.user_member_company_name)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Status'
                        value={
                          data.duplicate_certificate_application_status
                            ? getStatusLabel(
                              data.duplicate_certificate_application_status,
                            )
                            : 'N/A'
                        }
                      />
                    </Grid>
                  </Box>

                  <Divider sx={{ borderColor: '#f1f5f9' }} />

                  {/* Section 2: Uploaded Documents */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 3.5,
                          height: 15,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        variant='subtitle2'
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          letterSpacing: 0.1,
                        }}
                      >
                        Uploaded Documents
                      </Typography>
                    </Box>
                    <Grid container rowSpacing={0.75} columnSpacing={2}>
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Civil Aviation'
                        value={
                          data?.duplicate_certificate_application_civil_aviation ? (
                            <CommonTypoGraphy
                              name='View Civil Aviation'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  data.duplicate_certificate_application_civil_aviation,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Trade License'
                        value={
                          data?.duplicate_certificate_application_trade_license ? (
                            <CommonTypoGraphy
                              name='View Trade License'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  data.duplicate_certificate_application_trade_license,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='GD Copy'
                        value={
                          data?.duplicate_certificate_application_gd_copy ? (
                            <CommonTypoGraphy
                              name='View GD Copy'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  data.duplicate_certificate_application_gd_copy,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Application File'
                        value={
                          data?.duplicate_certificate_application_application_file ? (
                            <CommonTypoGraphy
                              name='View Application'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  data.duplicate_certificate_application_application_file,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                    </Grid>
                  </Box>

                  {/* Section 3: Payment Info */}
                  {data?.paymentInfo && (
                    <>
                      <Divider sx={{ borderColor: '#f1f5f9' }} />
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Box
                            sx={{
                              width: 3.5,
                              height: 15,
                              bgcolor: 'primary.main',
                              borderRadius: 1,
                            }}
                          />
                          <Typography
                            variant='subtitle2'
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.925rem',
                              color: '#0f172a',
                              letterSpacing: 0.1,
                            }}
                          >
                            Payment Information
                          </Typography>
                          <Chip
                            label={
                              data?.paymentInfo?.type === 'online'
                                ? 'Online Payment'
                                : 'Offline Payment'
                            }
                            size='small'
                            color={data?.paymentInfo?.type === 'online' ? 'primary' : 'default'}
                            variant='outlined'
                            sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600, ml: 0.5 }}
                          />
                        </Box>
                        <OfflinePaymentCardInformation data={data?.paymentInfo} />
                      </Box>
                    </>
                  )}
                </Box>
              ) : (
                <Typography
                  variant='body1'
                  fontWeight='bold'
                  color='text.secondary'
                  textAlign='center'
                  py={4}
                >
                  No Data found
                </Typography>
              )}
            </CardContent>
          </Card>

          <ModalsWrapper
            modalData={{ title: 'View Document' }}
            setShowModal={setOpen}
            showModal={open}
          >
            <PdfAndImageView file={imageFile} />
          </ModalsWrapper>
        </Box>
      ) : (
        <PageLoader />
      )}
    </>
  );
};

export default SingleDuplicateCertificateApplication;

